import { useContext, useEffect, useMemo } from 'react';
import { match } from 'path-to-regexp';
import { Paths } from '../../Common/Paths';
import type { AspNetRolesMenuItemsPermissions } from '../../Models/Classes';
import { PermissionContext } from './PermissionContextValue';

export const usePermissions = () => {
    const context = useContext(PermissionContext);
    if (!context) {
        throw new Error('usePermissions must be used within a PermissionProvider');
    }
    return context;
};

export const usePageID = () => {
    const { items, pageID, setPageID } = usePermissions();

    const findPageID = useMemo(() => {
        const currentPath = window.location.pathname;
        const matchedItem = items.find(item => {
            const pathValue = Object.values(Paths).find(path => {
                const matcher = match(path.value, { decode: decodeURIComponent });
                return matcher(currentPath) ? path : undefined;
            });
            const entry = Object.entries(Paths).find(([, path]) => path.value === pathValue?.value);
            return item.path === (entry?.[0] ?? null);
        });

        return matchedItem?.idItem ?? null;
    }, [items]);

    useEffect(() => {
        if (findPageID && findPageID !== pageID) {
            setPageID(findPageID);
        }
    }, [findPageID, pageID, setPageID]);

    return { pageID, setPageID };
};

export const useHasPermission = (requiredPermission: AspNetRolesMenuItemsPermissions): boolean => {
    const { permissions } = usePermissions();
    return useMemo(() => {
        if (requiredPermission.idItem === '0') {
            return permissions.some(permission =>
                permission.idAspNetRole === requiredPermission.idAspNetRole &&
                permission.idPermission === requiredPermission.idPermission
            );
        }
        return permissions.some(permission =>
            permission.idAspNetRole === requiredPermission.idAspNetRole &&
            permission.idItem === requiredPermission.idItem &&
            permission.idPermission === requiredPermission.idPermission
        );
    }, [permissions, requiredPermission]);
};
